/**
 * Quote-status mapping for the customer quote-detail surface (Phase 5.5).
 *
 * Single source of truth referenced by CONTEXT D-09 through D-12 + D-18. The
 * downstream view (`QuoteDetailView` / `QuoteDetail` body) reads pill key +
 * display mode through this module rather than inlining `quote.status ===
 * 'expired'` at render sites (D-43 SSO rule from Phase 4.6, carried forward).
 *
 * Two boundaries this module owns:
 *
 *   1. The `pending` / `denied` collapse to a single `'pending'`
 *      {@link StatusPillKey} (D-12 — legacy parity, identical chrome, NO
 *      "previously declined" affordance). The customer cannot tell from the
 *      detail page which were previously declined; that's deliberate.
 *
 *   2. The `draft` → not-applicable pill mapping (the page renders the
 *      not-found card per D-11). {@link mapQuoteStatusToPillKey} returns
 *      `null` for `draft` (and any unrecognised status); the view consumes
 *      `mapQuoteStatusToDisplayMode` to decide which body to render and
 *      gates pill rendering on `pillKey !== null`.
 *
 * Defensive defaults (per Threat T-05.5-04):
 *
 *   - Unknown status → `null` pill (never silently misclassify).
 *   - Unknown status → `'live'` display mode (never silently hide the page).
 *
 * The `StatusPillKey` union itself (in `./appointment-status`) is extended
 * with `'expired'` per D-18 and `'quote-accepted'` per TC-708 D-13 (green,
 * distinct from the blue appointment `'accepted'`); `'pending'` is reused.
 */

import type { StatusPillKey } from './appointment-status';

/**
 * Display-mode discriminator consumed by the QuoteDetail view to choose
 * between (a) the live quote body, (b) the post-accept success card,
 * (c) the expired notice, or (d) the not-found card. See D-09/D-10/D-11.
 */
export type QuoteDisplayMode = 'live' | 'success' | 'expired' | 'not-found';

/**
 * Map an API quote-status string to the matching {@link StatusPillKey},
 * or `null` when no pill should render (D-11 draft + defensive unknown).
 *
 * D-12: `pending` and `denied` BOTH collapse to `'pending'` — legacy parity,
 * identical chrome. The customer cannot distinguish previously-declined
 * quotes from the detail page; that's deliberate.
 *
 * The lower-casing is defensive; the adapter already normalises status at
 * the wire boundary (mirrors `appointment-status.ts` `normaliseStatus`).
 */
export function mapQuoteStatusToPillKey(status: string): StatusPillKey | null {
  const normalised = (status ?? '').toLowerCase();
  if (normalised === 'pending' || normalised === 'denied') return 'pending';
  if (normalised === 'accepted') return 'quote-accepted';
  if (normalised === 'expired') return 'expired';
  // `draft` and any unknown status return null — never silently misclassify.
  // The view renders the not-found card (for draft) or the live body (for
  // unknowns, per the `live` default in `mapQuoteStatusToDisplayMode`) and
  // omits the pill via `v-if="pillKey !== null"`.
  return null;
}

/**
 * Map an API quote-status string to the {@link QuoteDisplayMode} the view
 * uses to gate body rendering.
 *
 * Defensive default is `'live'` (never silently hide the page) per
 * Threat T-05.5-04 — an unknown status keeps the live body rendering so the
 * customer can still see the quote and proceed.
 */
export function mapQuoteStatusToDisplayMode(status: string): QuoteDisplayMode {
  const normalised = (status ?? '').toLowerCase();
  if (normalised === 'accepted') return 'success';
  if (normalised === 'expired') return 'expired';
  if (normalised === 'draft') return 'not-found';
  // pending, denied, and any unknown status → 'live'.
  return 'live';
}
